import React, {
  memo,
  useState,
} from 'react';
import Container from '@common/components/container';
import type { ExtendedUser, LoggedUser, Organisation } from '@common/types/objects';
import { useTranslation } from 'react-i18next';
import AddNoteForm from '@modules/core/forms/add-note/add-note';
import { APIResponseNotesList, NotesFormData } from '@modules/core/types';
import { Note } from '@modules/core/components/note';
import Bar from '@common/components/bar';
import Card from '@common/components/card';
import { Button } from '@common/components/button';
import Spinner from '@common/components/spinner';
import { useRequest } from '@common/services/api';
import { createNote, deleteNote, getNotesEndpoint } from './api/notes-api';
import { NoNotes } from './no-notes';
import { AlertService } from '@common/services/alert';
import { useSelector } from 'react-redux';
import { StoreState } from '@common/types/store';


type NotesContainerProps = {
  user: ExtendedUser | LoggedUser;
  organisation: Organisation;
  canCreateNotes: boolean;
};

export const AdminNotes = memo(({ user, organisation, canCreateNotes }: NotesContainerProps) => {
  const { t } = useTranslation();
  const [isNoteAddModalVisible, setIsNoteAddModalVisible] = useState(false);
  const loggedUser = useSelector((state: StoreState) => state.loggedUser.user);

  const apiConfig = {
    transform: (res: APIResponseNotesList) => {
      const notesWithUsers = res.data.map((note) => {
        return {
          ...note,
          created_by: res.meta.related.users.find((creator) => creator.id === note.created_by) || note.created_by,
          deleted_by: res.meta.related.users.find((creator) => creator.id === note.deleted_by) || note.deleted_by,
        };
      });

      return {
        ...res,
        data: notesWithUsers,
      };
    },
  };

  const {
    data: notes,
    setData: setNotes,
    loading,
    refetch,
  } = useRequest<APIResponseNotesList>(getNotesEndpoint(organisation.id, user.id), apiConfig);


  const handleAddNote = async (formData: NotesFormData) => {
    await createNote(organisation.id, user.id, formData);
    setIsNoteAddModalVisible(false);
    refetch();
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      const res = await deleteNote(organisation.id, user.id, noteId);
      if (res.data.hard_delete) {
        setNotes(notes.filter((note) => note.id !== noteId));
      } else {
        setNotes(notes.map((note) => (note.id === noteId ? {
 ...note, text: '', attachments: [], deleted_by: loggedUser, deleted_at: new Date().toString()
} : note)));
      }
      AlertService.success(t('core:note_deleted_successfully'));
    } catch (error: any) {
      AlertService.forStatus(error.status, {
        warning: t('core:note_deleted_warning'),
        error: t('core:note_deleted_error'),
      });
    }
  };

  if (loading) {
    return <Spinner centered size="large" />;
  }

  return (
    <>
      {isNoteAddModalVisible && (
        <AddNoteForm
          isVisible={isNoteAddModalVisible}
          onSave={handleAddNote}
          handleClose={() => setIsNoteAddModalVisible(false)}
        />
      )}

      {
        notes.length === 0 && !loading ? (
          <NoNotes
            canCreateNotes={canCreateNotes}
            setIsNoteAddModalVisible={setIsNoteAddModalVisible}
          />
        ) : (
          <Container.Content>
            <Bar className="AdminNotes__Header">
              <h2>{t('core:profile_tabs_label_notes')}</h2>
              {canCreateNotes && (
                <Button
                  data-testid="add-note-button"
                  onClick={() => setIsNoteAddModalVisible(true)}
                  type="primary" size="large" icon="add"
                >
                  {t('core:add_note')}
                </Button>
              )}

            </Bar>
            <Container.Column>
              <Card containerClassName="AdminNotes__Card">
                {notes.length > 0 && notes.map((note) => (
                  <Note key={note.id} note={note} showVisibility={canCreateNotes} onDelete={() => handleDeleteNote(note.id)} />
                ))}
              </Card>
            </Container.Column>
          </Container.Content>
        )
      }
    </>);
});
